import postValidateReferralCodeRequest, {
  describeReferralValidationFailure,
  ReferralValidation,
  ReferralValidationFailure,
  ValidationResult,
} from 'features/signup/helpers/referrals.helpers';
import { useContext, useEffect, useState } from 'react';
import {
  EnvironmentContext,
  EnvironmentContextProps,
  getReferralLambdaUrl,
} from 'shared/providers/environment';
import { logError } from 'shared/utils/sentry/sentry';

const INVALID_CODE_OUTCOMES: ReadonlySet<string> = new Set([
  ValidationResult.EXPIRED,
  ValidationResult.INVALID,
]);

interface ValidatedReferralCode {
  referralCode: string;
  validation: ReferralValidation;
}

const useReferrals = (referralCode: string | undefined) => {
  const [validatedReferralCode, setValidatedReferralCode] =
    useState<ValidatedReferralCode>();
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);

  const lambdaUrl = getReferralLambdaUrl(currentEnv);

  useEffect(() => {
    if (!referralCode) {
      return;
    }

    const abortController = new AbortController();

    const validateReferralCode = async () => {
      let validation: ReferralValidation;
      try {
        validation = await postValidateReferralCodeRequest(
          lambdaUrl,
          { referralCode },
          abortController.signal,
        );
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        logError(error);
        validation = {
          outcome: ReferralValidationFailure.UNAVAILABLE,
          failureReason: describeReferralValidationFailure(error),
        };
      }
      setValidatedReferralCode({ referralCode, validation });
    };

    void validateReferralCode();

    return () => {
      abortController.abort();
    };
  }, [lambdaUrl, referralCode]);

  const validation =
    referralCode && validatedReferralCode?.referralCode === referralCode
      ? validatedReferralCode.validation
      : undefined;
  const outcome = validation?.outcome;

  return {
    isFetching: Boolean(referralCode) && validation === undefined,
    isValidReferral: outcome === ValidationResult.VALID,
    hasInvalidReferralCode:
      outcome !== undefined && INVALID_CODE_OUTCOMES.has(outcome),
    userName: validation?.senderName ?? '',
    validation,
  };
};

export default useReferrals;
